import React, { useState } from 'react'
import { useDesignSystems } from './hooks/useDesignSystems'
import { DesignSystemPanel } from './components/DesignSystemPanel'
import { MotifPicker } from './components/MotifPicker'
import { TokenEditor } from './components/TokenEditor'
import { Preview } from './components/Preview'
import { ExportPanel } from './components/ExportPanel'
import { Modal } from './components/Modal'

export default function App() {
  const {
    systems,
    activeSystem,
    resolvedTokens,
    selectSystem,
    createSystem,
    setMotif,
    updateToken,
    setThemeName,
    setSystemName,
    resetOverrides,
    hasOverrides,
  } = useDesignSystems()

  const [isExportOpen, setIsExportOpen] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Design System Builder</h1>
              <p className="text-sm text-gray-500">
                Create custom design tokens for your project
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsExportOpen(true)}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-md hover:bg-gray-800 cursor-pointer"
              >
                Export
              </button>
              <a
                href="/"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                Back to Nimble Labs
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
          {/* Left Column - Controls */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <DesignSystemPanel
                systems={systems}
                activeSystem={activeSystem}
                onSelect={selectSystem}
                onCreate={createSystem}
                onRename={setSystemName}
              />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <MotifPicker selectedMotifId={activeSystem.baseMotifId} onSelect={setMotif} />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <TokenEditor
                tokens={resolvedTokens}
                onUpdate={updateToken}
                hasOverrides={hasOverrides}
                onReset={resetOverrides}
              />
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Preview tokens={resolvedTokens} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-sm text-gray-500 text-center">
            Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline cursor-pointer">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>

      <Modal
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        title="Export Design System"
        size="lg"
      >
        <ExportPanel
          tokens={resolvedTokens}
          themeName={activeSystem.themeName}
          onThemeNameChange={setThemeName}
        />
      </Modal>
    </div>
  )
}
